import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Chip, Dialog, DialogContent, DialogTitle, FormControl,
  IconButton, InputAdornment, InputLabel, MenuItem, Paper,
  Select, SelectChangeEvent, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert,
  Button, Divider,
} from '@mui/material';
import {
  Search as SearchIcon, Block as BlockIcon, CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon, Download, Email as EmailIcon,
  Phone as PhoneIcon, DateRange as CalendarIcon, Star as CrownIcon,
  Close as CloseIcon, ShoppingCart as OrderIcon, TrendingUp as TrendingIcon,
  Feedback as FeedbackIcon, Repeat as RepeatIcon,
  Grade as RatingIcon, SupportAgent as TicketIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

const SUPER_ADMIN_EMAIL = 'info@simploona.com';

// Human-readable label + color for a support ticket's status, kept in sync with
// the Support page so the same ticket never reads differently in the two places
// (e.g. an "in-progress" ticket must not show as raw "in-progress" here while
// the Support page calls it something else).
const TICKET_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  'in-progress': 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};
const ticketStatusColor = (status?: string): 'warning' | 'info' | 'success' | 'default' => {
  if (status === 'in-progress') return 'info';
  if (status === 'resolved') return 'success';
  if (status === 'closed') return 'default';
  return 'warning';
};

interface User {
  _id?: string;
  id?: string;
  name: string;
  email: string;
  phone?: string;
  role: 'customer' | 'merchant' | 'admin' | 'delivery';
  status: 'active' | 'banned' | 'pending';
  createdAt?: string;
  joinedAt?: string;
  totalOrders?: number;
  totalSpent?: number;
  rating?: { average: number; count: number };
  activeBoxes?: number;
  totalSales?: number;
  description?: string;
  category?: string;
  supportTickets?: number;
  activity?: {
    totalOrders: number;
    cancelledOrders: number;
    cancellationRate: number;
    totalSpent: number;
    supportTickets: number;
    openSupportTickets: number;
    returns: number;
  };
  activityAssessment?: { level: 'normal' | 'watch' | 'review'; reasons: string[] };
  activeSupportTickets?: Array<{
    ticketNumber: string;
    subject: string;
    status: string;
    priority: string;
  }>;
}

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [ticketCounts, setTicketCounts] = useState<{ [key: string]: number }>({});
  const [ticketsByUser, setTicketsByUser] = useState<{ [key: string]: any[] }>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; active: number; banned: number; customers: number; merchants: number; admins: number; delivery: number } | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Role/status/search now filter server-side (the table is paginated, so
  // filtering only the fetched page — as this used to do — hid any role
  // that wasn't among the most-recently-created rows). Reset to page 1
  // whenever a filter changes so the new query starts from the top.
  useEffect(() => {
    setPage(1);
  }, [roleFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
    fetchTicketCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, roleFilter, statusFilter, debouncedSearch]);

  const fetchTicketCounts = async () => {
    try {
      const response = await axios.get('/api/operations/support/tickets');
      const tickets = response.data.tickets || [];
      const counts: { [key: string]: number } = {};
      const ticketsByName: { [key: string]: any[] } = {};
      tickets.forEach((ticket: any) => {
        counts[ticket.userName] = (counts[ticket.userName] || 0) + 1;
        if (ticket.status === 'open' || ticket.status === 'in-progress') {
          ticketsByName[ticket.userName] = [...(ticketsByName[ticket.userName] || []), ticket];
        }
      });
      setTicketCounts(counts);
      setTicketsByUser(ticketsByName);
    } catch (err) {
      console.error('Error fetching ticket counts:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users', {
        params: {
          page,
          limit: rowsPerPage,
          role: roleFilter !== 'All' ? roleFilter : undefined,
          status: statusFilter !== 'All' ? statusFilter : undefined,
          search: debouncedSearch || undefined,
        },
      });
      const usersData = response.data.users || [];
      setTotal(response.data.total ?? usersData.length);
      setServerStats(response.data.stats || null);
      const usersWithTickets = usersData.map((user: User) => ({
        ...user,
        supportTickets: ticketCounts[user.name] || 0,
      }));
      setUsers(usersWithTickets);
      setError(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = (user: User) => user.email === SUPER_ADMIN_EMAIL;

  const handleBanUser = async (userId: string, user: User) => {
    if (isSuperAdmin(user)) {
      setSnackbar({ open: true, message: 'Cannot ban super admin', severity: 'error' });
      return;
    }

    try {
      await axios.put(`/api/admin/users/${userId}/ban`);
      fetchUsers();
      setSnackbar({ open: true, message: `User ${user.status === 'active' ? 'banned' : 'unbanned'} successfully`, severity: 'success' });
      setDetailsOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update user', severity: 'error' });
    }
  };

  // The backend already applied role/status/search and pagination, so this
  // page's rows ARE the filtered results — no client-side re-filtering.
  const filteredUsers = users;

  // Prefer the backend's platform-wide counts (every user, not just this
  // page) so the role cards don't look like a role has zero users just
  // because none of them landed on the current page.
  const stats = serverStats || {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    banned: users.filter(u => u.status === 'banned').length,
    customers: users.filter(u => u.role === 'customer').length,
    merchants: users.filter(u => u.role === 'merchant').length,
    admins: users.filter(u => u.role === 'admin').length,
    delivery: users.filter(u => u.role === 'delivery').length,
  };

  const handleExportData = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Role', 'Status', 'Joined Date', 'Total Orders', 'Total Spent'],
      ...filteredUsers.map(u => [
        u.name,
        u.email,
        u.phone || 'N/A',
        u.role,
        u.status,
        new Date(u.createdAt || '').toLocaleDateString(),
        u.totalOrders || 0,
        `OMR ${(u.totalSpent || 0).toFixed(2)}`,
      ]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    setSnackbar({ open: true, message: 'Users exported successfully', severity: 'success' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>User Management</Typography>
          <Typography color="text.secondary">Manage users, roles, and permissions.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Download />} onClick={handleExportData} disabled={filteredUsers.length === 0}>
          Export Users
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Users</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Active</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.active}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Banned</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.banned}</Typography>
        </Paper>
      </Box>

      {/* Role Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Customers</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>{stats.customers}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Merchants</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.merchants}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Delivery</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.delivery}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Admins</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>{stats.admins}</Typography>
        </Paper>
      </Box>

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select value={roleFilter} label="Role" onChange={(e: SelectChangeEvent) => setRoleFilter(e.target.value)}>
              {['All', 'customer', 'merchant', 'admin', 'delivery'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              {['All', 'active', 'banned', 'pending'].map((opt) => (
                <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>Unable to load users. Check that the backend is running.</Alert>}

        {!error && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Joined</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography>Loading users...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredUsers.map((user) => {
                  const userId = user._id || user.id;
                  const isSuper = isSuperAdmin(user);
                  return (
                    <TableRow key={userId} hover sx={{ bgcolor: isSuper ? 'rgba(255, 193, 7, 0.08)' : 'transparent' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isSuper && <CrownIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                          <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          color={
                            user.role === 'merchant' ? 'primary' :
                              user.role === 'admin' ? 'secondary' :
                                user.role === 'delivery' ? 'success' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          size="small"
                          color={user.status === 'active' ? 'success' : 'error'}
                          icon={user.status === 'active' ? <CheckCircleIcon /> : <BlockIcon />}
                        />
                      </TableCell>
                      <TableCell>{new Date(user.createdAt || '').toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => {
                          setSelectedUser({
                            ...user,
                            supportTickets: user.activity?.supportTickets || ticketCounts[user.name] || 0,
                            activeSupportTickets: ticketsByUser[user.name] || [],
                          });
                          setDetailsOpen(true);
                        }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {!isSuper && (
                          <IconButton
                            size="small"
                            color={user.status === 'active' ? 'error' : 'success'}
                            onClick={() => handleBanUser(userId || '', user)}
                          >
                            {user.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #eee' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredUsers.length} of {users.length} users
          </Typography>
        </Box>
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* User Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          User Details
          <IconButton onClick={() => setDetailsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* Basic Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{selectedUser.name}</Typography>
                  {isSuperAdmin(selectedUser) && (
                    <Chip icon={<CrownIcon />} label="Super Admin" size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography>{selectedUser.email}</Typography>
                </Box>
              </Box>

              {selectedUser.phone && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography>{selectedUser.phone}</Typography>
                  </Box>
                </Box>
              )}

              <Divider />

              {/* Merchant Specific Info */}
              {selectedUser.role === 'merchant' && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Merchant Details
                  </Typography>

                  {selectedUser.description && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{selectedUser.description}</Typography>
                    </Box>
                  )}

                  {selectedUser.category && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                      <Chip label={selectedUser.category} size="small" variant="outlined" sx={{ mt: 1 }} />
                    </Box>
                  )}

                  <Divider />

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <TrendingIcon />
                      </Box>
                      <Typography variant="caption">Active Boxes</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {selectedUser.activeBoxes || 0}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <OrderIcon />
                      </Box>
                      <Typography variant="caption">Total Sales</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        <RialSymbol /> {(selectedUser.totalSales || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <RatingIcon />
                      </Box>
                      <Typography variant="caption">Rating</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {(selectedUser.rating?.average || 0).toFixed(1)}⭐
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        ({selectedUser.rating?.count || 0} reviews)
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <FeedbackIcon />
                      </Box>
                      <Typography variant="caption">Best Selling</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        Coming Soon
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}

              {/* Customer Specific Info */}
              {selectedUser.role === 'customer' && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Customer Activity
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <OrderIcon />
                      </Box>
                      <Typography variant="caption">Total Orders</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {selectedUser.activity?.totalOrders || selectedUser.totalOrders || 0}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <TrendingIcon />
                      </Box>
                      <Typography variant="caption">Total Spent</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        <RialSymbol /> {(selectedUser.activity?.totalSpent || selectedUser.totalSpent || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <RepeatIcon />
                      </Box>
                      <Typography variant="caption">Repeat Orders</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {selectedUser.activity?.totalOrders
                          ? `${Math.max(selectedUser.activity.totalOrders - selectedUser.activity.cancelledOrders, 0)} completed`
                          : 'No order history'}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <FeedbackIcon />
                      </Box>
                      <Typography variant="caption">Reviews</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {selectedUser.activity?.returns || 0}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}

              <Divider />

              {/* Support Tickets */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                  Support History
                </Typography>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <TicketIcon />
                  </Box>
                  <Typography variant="caption">Support Tickets Created</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {selectedUser.activity?.supportTickets || selectedUser.supportTickets || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {selectedUser.activity?.openSupportTickets || 0} currently open
                  </Typography>
                  {(selectedUser.activeSupportTickets || []).length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2, textAlign: 'left' }}>
                      <Typography variant="caption" color="text.secondary">Active ticket numbers</Typography>
                      {selectedUser.activeSupportTickets?.map((ticket) => (
                        <Box key={ticket.ticketNumber} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Button
                              size="small"
                              onClick={() => navigate(`/support?ticket=${encodeURIComponent(ticket.ticketNumber)}`)}
                              sx={{ minWidth: 0, p: 0, fontWeight: 700, justifyContent: 'flex-start' }}
                            >
                              {ticket.ticketNumber}
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ticket.subject}
                            </Typography>
                          </Box>
                          <Chip label={TICKET_STATUS_LABEL[ticket.status] || ticket.status} size="small" color={ticketStatusColor(ticket.status)} />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                  Activity Review
                </Typography>
                <Paper sx={{ p: 2, bgcolor: selectedUser.activityAssessment?.level === 'review' ? 'error.light' : selectedUser.activityAssessment?.level === 'watch' ? 'warning.light' : 'success.light' }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {selectedUser.activityAssessment?.level === 'review' ? 'Review recommended' : selectedUser.activityAssessment?.level === 'watch' ? 'Keep an eye on activity' : 'No unusual activity detected'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedUser.activity?.cancellationRate
                      ? `${(selectedUser.activity.cancellationRate * 100).toFixed(0)}% of orders cancelled (${selectedUser.activity.cancelledOrders} of ${selectedUser.activity.totalOrders}).`
                      : 'No cancelled orders recorded.'}
                  </Typography>
                  {selectedUser.activityAssessment?.reasons.map((reason) => (
                    <Typography key={reason} variant="body2">• {reason}</Typography>
                  ))}
                </Paper>
              </Box>

              <Divider />

              {/* Status Info */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                  <Chip
                    label={selectedUser.role}
                    color={selectedUser.role === 'merchant' ? 'primary' : selectedUser.role === 'admin' ? 'secondary' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedUser.status}
                    color={selectedUser.status === 'active' ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Joined</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography>{new Date(selectedUser.createdAt || '').toLocaleString()}</Typography>
                </Box>
              </Box>

              {/* Actions */}
              {!isSuperAdmin(selectedUser) && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color={selectedUser.status === 'active' ? 'error' : 'success'}
                      onClick={() => handleBanUser(selectedUser._id || selectedUser.id || '', selectedUser)}
                    >
                      {selectedUser.status === 'active' ? 'Ban User' : 'Unban User'}
                    </Button>
                  </Box>
                </>
              )}

              {isSuperAdmin(selectedUser) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This is a super admin account and cannot be deleted or banned.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
